<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { NIcon } from 'naive-ui';
import { IconDocumentText } from '@/components/design/icons';
import CkEditorInput from '@/components/CkeditorInput.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaSaveState from '@/components/design/composite/GaiaSaveState.vue';
import GaiaWorkspacePane from '@/components/design/review/GaiaWorkspacePane.vue';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';

// Pure view over the store: draft, save state and the debounced save all live
// in reviewProjectStore because a pending save must survive this pane being
// closed and reopened (same setup as the notes pane).
const props = defineProps({
  projectUid: {
    type: String,
    required: true,
  },
  /** Expanded, the report has the workspace's right column to itself. */
  expanded: {
    type: Boolean,
    default: false,
  },
  /**
   * Passed straight to the pane — a space-separated edge list. The workspace
   * decides it: which edges need a rule depends on whether this pane is sharing
   * its band with a sibling or filling the column alone.
   */
  divider: {
    type: String,
    default: 'top',
  },
});

const emit = defineEmits<{ close: []; 'update:expanded': [expanded: boolean] }>();

const reviewProjectStore = useReviewProjectStore();

const loadFailed = ref(false);

function load(): void {
  loadFailed.value = false;
  reviewProjectStore.initReport(props.projectUid).catch(() => {
    loadFailed.value = true;
  });
}

onMounted(load);

const lastSaved = computed(() => {
  const report = reviewProjectStore.report;
  if (!report) return '';
  return formatDateTimeForTable(report.updated_at ?? report.created_at);
});

const draft = computed({
  get: () => reviewProjectStore.reportDraft,
  set: value => {
    reviewProjectStore.setReportDraft(value);
  },
});
</script>

<template>
  <gaia-workspace-pane
    title="Report"
    tone="soft"
    :divider="props.divider"
    :scroll-body="false"
    pad-body
    expandable
    closable
    :expanded="props.expanded"
    expand-label="Expand over the analysis"
    contract-label="Show the analysis"
    data-tour="rp.report"
    @update:expanded="value => emit('update:expanded', value)"
    @close="emit('close')"
  >
    <template #icon>
      <n-icon :component="IconDocumentText" :size="15" />
    </template>

    <template v-if="reviewProjectStore.report !== null && !loadFailed" #toolbar>
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-text size="micro" tone="subtle">Last saved {{ lastSaved }}</gaia-text>
        <!-- Hidden while the report agent runs — the draft was flushed at lock
             time, so there is nothing left for the user to send. -->
        <gaia-save-state
          :state="reviewProjectStore.reportSaveState"
          :disabled="reviewProjectStore.reportGenerationLocked"
          :style="{ marginLeft: 'auto' }"
          @save="reviewProjectStore.flushReport"
        />
      </n-flex>
    </template>

    <n-flex v-if="loadFailed" vertical align="center" justify="center" :size="12" style="flex: 1">
      <gaia-text tone="tertiary">Failed to load the report.</gaia-text>
      <gaia-button size="small" @click="load">Retry</gaia-button>
    </n-flex>

    <n-flex
      v-else-if="reviewProjectStore.report === null"
      align="center"
      justify="center"
      style="flex: 1"
    >
      <gaia-spinner />
    </n-flex>

    <n-flex v-else vertical :size="10" style="flex: 1; min-height: 0">
      <gaia-banner
        v-if="reviewProjectStore.reportGenerationLocked"
        type="info"
        title="The report agent is writing the report…"
      >
        The editor is read-only until the run finishes.
      </gaia-banner>
      <gaia-banner
        v-else-if="reviewProjectStore.reportGenerationError"
        type="warning"
        closable
        :title="reviewProjectStore.reportGenerationError"
        @close="reviewProjectStore.reportGenerationError = null"
      />
      <div class="report-editor">
        <ck-editor-input
          v-model="draft"
          extended-mode
          :read-only-mode="reviewProjectStore.reportGenerationLocked"
          placeholder="Write the report…"
        />
      </div>
    </n-flex>
  </gaia-workspace-pane>
</template>

<style scoped>
.report-editor {
  flex: 1;
  min-height: 0;
}

/* CkeditorInput forces a fixed 200px content height (inline styles from JS);
   in the pane the editor should fill the remaining height and scroll inside. */
.report-editor :deep(.ck-editor) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.report-editor :deep(.ck-editor__main) {
  flex: 1;
  min-height: 0;
}

.report-editor :deep(.ck-editor__editable) {
  height: 100% !important;
  min-height: 0 !important;
  overflow-y: auto;
  box-sizing: border-box;
}
</style>
